import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import { previewCommissionRule } from '../../core/api'
import { ICommissionRulePreview } from '../../core/response.types'
import {
  CashboxKind,
  CommissionCurrency,
  CommissionDirection,
  CURRENCY_LABELS,
  DIRECTION_LABELS,
  KIND_LABELS,
} from '../../core/consts'
import MerchantFilter from '../../../../../../common/filter/MerchantFilter'
import PaymentMethodsFilter from '../../../../../../common/filter/PaymentMethodsFilter'
import { SelectStyles } from '../../../../../../../components/Select'
import { useThemeMode } from '../../../../../../../_metronic/partials'
import { getListCashBoxesForSelect } from '../../../../../../common/core/_requests'

interface Props {
  show:    boolean
  onClose: () => void
}

const directionOptions = Object.entries(DIRECTION_LABELS).map(([value, label]) => ({ value, label }))
const currencyOptions  = Object.entries(CURRENCY_LABELS).map(([value, label]) => ({ value, label }))
const kindOptions      = Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))

const CommissionPreviewModal: React.FC<Props> = ({ show, onClose }) => {
  const { mode: theme } = useThemeMode()
  const selectStyles = SelectStyles(theme)

  const [direction, setDirection]       = useState('')
  const [currency, setCurrency]         = useState('')
  const [merchantToken, setMerchant]    = useState('')
  const [cashBoxId, setCashBoxId]       = useState<number | ''>('')
  const [cashboxKind, setCashboxKind]   = useState('')
  const [paymentMethod, setPayMethod]   = useState('')
  const [amount, setAmount]             = useState('')

  const [cashBoxOptions, setCashBoxOptions] = useState<{ value: number; label: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<ICommissionRulePreview | null>(null)
  const [error, setError]     = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getListCashBoxesForSelect()
      .then((resp: any) => {
        if (cancelled) return
        const list = resp?.data?.cashBoxes ?? resp?.data?.data ?? []
        setCashBoxOptions(list.map((cb: any) => ({ value: cb.id, label: cb.title ?? `#${cb.id}` })))
      })
      .catch(() => setCashBoxOptions([]))
    return () => { cancelled = true }
  }, [])

  // reset state each time the modal opens
  useEffect(() => {
    if (show) {
      setDirection(''); setCurrency(''); setMerchant(''); setCashBoxId('')
      setCashboxKind(''); setPayMethod(''); setAmount('')
      setPreview(null); setError(null)
    }
  }, [show])

  const canRun = !!direction && !!currency && amount.trim() !== ''

  const run = async () => {
    if (!canRun) return
    setLoading(true)
    setError(null)
    try {
      const resp = await previewCommissionRule({
        direction:     direction as CommissionDirection,
        currency:      currency  as CommissionCurrency,
        merchantToken: merchantToken || undefined,
        cashBoxId:     cashBoxId === '' ? undefined : Number(cashBoxId),
        cashboxKind:   (cashboxKind || undefined) as CashboxKind | undefined,
        paymentMethod: paymentMethod || undefined,
        amount,
      })
      if (!resp.data?.success || !resp.data.data) {
        const failure = resp.data as { success: false; errors: { general_error?: string } } | undefined
        setError(failure?.errors?.general_error ?? 'Не удалось рассчитать комиссию')
        setPreview(null)
        return
      }
      setPreview(resp.data.data)
    } catch (e: any) {
      setError(e?.response?.data?.errors?.general_error ?? 'Ошибка запроса')
      setPreview(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <div className='modal-header'>
        <h2 className='modal-title'>Проверка комиссии</h2>
      </div>
      <div className='modal-body'>
        <p className='text-muted'>
          Расчёт по текущим сохранённым правилам — показывает, какое правило сработает
          для заданной операции. Незасохранённые черновики не учитываются.
        </p>

        <div className='row g-3'>
          <div className='col-md-6'>
            <label className='form-label fw-bold'>Направление *</label>
            <Select
              isClearable
              options={directionOptions}
              value={directionOptions.find((o) => o.value === direction) ?? null}
              onChange={(opt) => setDirection((opt as any)?.value ?? '')}
              placeholder='Выберите направление'
              styles={selectStyles}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-bold'>Валюта *</label>
            <Select
              isClearable
              options={currencyOptions}
              value={currencyOptions.find((o) => o.value === currency) ?? null}
              onChange={(opt) => setCurrency((opt as any)?.value ?? '')}
              placeholder='Выберите валюту'
              styles={selectStyles}
            />
          </div>
        </div>

        <div className='row g-3 mt-1'>
          <div className='col-md-4'>
            <label className='form-label fw-bold'>Мерчант</label>
            <MerchantFilter
              value={merchantToken ? [merchantToken] : []}
              changeSelect={(d) => setMerchant((d.value as string[])[0] ?? '')}
            />
          </div>
          <div className='col-md-4'>
            <label className='form-label fw-bold'>Касса</label>
            <Select
              isClearable
              isDisabled={!!cashboxKind}
              options={cashBoxOptions}
              value={cashBoxOptions.find((o) => o.value === cashBoxId) ?? null}
              onChange={(opt) => setCashBoxId((opt as any)?.value ?? '')}
              placeholder='Выберите кассу'
              styles={selectStyles}
            />
          </div>
          <div className='col-md-4'>
            <label className='form-label fw-bold'>Тип кассы</label>
            <Select
              isClearable
              isDisabled={cashBoxId !== ''}
              options={kindOptions}
              value={kindOptions.find((o) => o.value === cashboxKind) ?? null}
              onChange={(opt) => setCashboxKind((opt as any)?.value ?? '')}
              placeholder='Выберите тип'
              styles={selectStyles}
            />
          </div>
        </div>

        <div className='row g-3 mt-1'>
          <div className='col-md-6'>
            <label className='form-label fw-bold'>Платёжный метод</label>
            <PaymentMethodsFilter
              value={paymentMethod ? [paymentMethod] : []}
              changeSelect={(d) => setPayMethod((d.value as string[])[0] ?? '')}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-bold'>Сумма *</label>
            <input
              className='form-control form-control-solid'
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder='1000.0000'
            />
          </div>
        </div>

        {error && <div className='alert alert-danger mt-4'>{error}</div>}

        {preview && (
          <div className='mt-4 p-3 bg-light rounded'>
            <div>
              Сработавшее правило: <strong>{preview.matchedRuleId ?? '—'}</strong>
              {preview.isDefault && <span className='badge badge-light-primary ms-2'>Системное</span>}
            </div>
            <div>
              Комиссия:{' '}
              <strong className={preview.feeKind === 'REBATE' ? 'text-danger' : ''}>
                {preview.fee}
              </strong>
              <small className='text-muted ms-2'>(minor: {preview.feeMinor})</small>
              {preview.feeKind === 'REBATE' && (
                <span className='badge badge-light-danger ms-2'>Ребейт — PS платит мерчанту</span>
              )}
            </div>
            <div>Сработавшие измерения: {preview.matchedDimensions.join(', ') || '—'}</div>
          </div>
        )}

        <div className='d-flex justify-content-end gap-2 mt-4'>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={loading}>
            Закрыть
          </button>
          <button type='button' className='btn btn-primary' disabled={!canRun || loading} onClick={run}>
            {loading && <span className='spinner-border spinner-border-sm me-2' />}
            Проверить комиссию
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default CommissionPreviewModal
